import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Skeleton,
  Button,
  Tooltip,
  IconButton,
  TextField,
} from '@mui/material';
import { motion } from 'framer-motion';
import Visibility from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Sidebar from './Sidebar.jsx';
import { fetchWorkflowRunHistory } from '../../apis/Dashboard.js';
import { cancelWorkflowSchedule } from '../../apis/Workflows.js';
import { toast } from 'react-toastify';
import LogsViewer from '../../workflows/LogsViewer';

const pad = (n) => String(n).padStart(2, '0');
const formatLocalDate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const normalizeDateValue = (value) => {
  if (!value) {
    return 'none';
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? 'invalid' : date.toISOString();
};

const statusColorMap = {
  SUCCESS: {
    bg: '#10B981',
    bgLight: '#D1FAE5',
    text: '#065F46',
    border: '#10B981',
  },
  FAILED: {
    bg: '#EF4444',
    bgLight: '#FEE2E2',
    text: '#991B1B',
    border: '#EF4444',
  },
  ACTIVE: {
    bg: '#3B82F6',
    bgLight: '#DBEAFE',
    text: '#1E40AF',
    border: '#3B82F6',
  },
  PARTIAL_SUCCESS: {
    bg: '#F59E0B',
    bgLight: '#FEF3C7',
    text: '#92400E',
    border: '#F59E0B',
  },
  CANCELLED: {
    bg: '#A3A3A3',
    bgLight: '#E5E7EB',
    text: '#1F2937',
    border: '#A3A3A3',
  },
};

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      type: 'spring',
      stiffness: 100,
    },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: index * 0.05,
      duration: 0.3,
      ease: 'easeOut',
    },
  }),
  hover: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
    },
  },
};

const ScheduleJob = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedWorkflowRunId, setSelectedWorkflowRunId] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'none',
  });

  
  const [canceling, setCanceling] = useState({});

  const [search, setSearch] = useState('');


  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    loadData();
  }, [limit]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
     
      const data = await fetchWorkflowRunHistory({
        is_scheduled: true,
        limit: Number(limit),
      });
      const list = Array.isArray(data) ? data : [];
      // Safety: if API ignores param, filter locally
      const scheduledOnly = list.filter((r) => !!r?.is_scheduled);
     
      const seenKeys = new Set();
      const dedupedRows = [];
      scheduledOnly.forEach((run) => {
        const nameKey = (run.workflow_name || '').toLowerCase();
        const createdKey = normalizeDateValue(
          run.metadata?.created_at || run.start_time,
        );
        const nextRunKey = normalizeDateValue(run.next_run_date);
        const endKey = normalizeDateValue(run.metadata?.end_time || run.end_time);
        const dedupeKey = `${nameKey}|${createdKey}|${nextRunKey}|${endKey}`;
        if (!seenKeys.has(dedupeKey)) {
          seenKeys.add(dedupeKey);
          dedupedRows.push(run);
        }
      });
      setRows(dedupedRows);
   
      setSortConfig({ column: null, direction: 'none' });
    } catch (e) {
      setError(e?.message || 'Failed to fetch scheduled jobs');
    } finally {
      setLoading(false);
    }
  };

  const getConnectionNames = (run) => {
    const meta = run?.metadata || {};
    const sources = Array.isArray(meta.sources) ? meta.sources : [];
    const destinations = Array.isArray(meta.destinations)
      ? meta.destinations
      : [];
    const sourceNames = sources.map((s) => s?.connection_name).filter(Boolean);
    const destNames = destinations
      .map((d) => d?.connection_name)
      .filter(Boolean);
    return {
      source: sourceNames.length > 0 ? sourceNames : ['-'],
      destination: destNames.length > 0 ? destNames : ['-'],
    };
  };

  const handleSort = (column) => {

    let newColumn = column;
    let direction = 'asc';

    if (sortConfig.column === newColumn && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (
      sortConfig.column === newColumn &&
      sortConfig.direction === 'desc'
    ) {
      direction = 'none';
      // When toggling off sorting, we unset the column completely.
      newColumn = null;
    }

    setSortConfig({ column: newColumn, direction });
  };


  const filteredRows = React.useMemo(() => {
    const query = search.trim().toLowerCase();
   
    const fromTs = fromDate ? new Date(fromDate).setHours(0, 0, 0, 0) : null;
    const toTs = toDate ? new Date(toDate).setHours(23, 59, 59, 999) : null;
    return rows.filter((r) => {
      // Name match for search
      const name = (r.workflow_name || '').toLowerCase();
      const nameMatch = !query || name.includes(query);
     
      let createdTime = 0;
      if (r.metadata?.created_at) {
        createdTime = new Date(r.metadata.created_at).getTime();
      } else if (r.start_time) {
        createdTime = new Date(r.start_time).getTime();
      }
      const dateMatch =
        (!fromTs || createdTime >= fromTs) && (!toTs || createdTime <= toTs);
      return nameMatch && dateMatch;
    });
  }, [rows, search, fromDate, toDate]);

  const sortedRows = React.useMemo(() => {
    if (sortConfig.direction === 'none' || !sortConfig.column) {
      return filteredRows;
    }

    return [...filteredRows].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.column) {
        case 'workflow_name':
          aValue = (a.workflow_name || '').toLowerCase();
          bValue = (b.workflow_name || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || a.run_status || '').toLowerCase();
          bValue = (b.status || b.run_status || '').toLowerCase();
          break;
        case 'created_date':
          aValue = a.metadata?.created_at
            ? new Date(a.metadata.created_at).getTime()
            : a.start_time
              ? new Date(a.start_time).getTime()
              : 0;
          bValue = b.metadata?.created_at
            ? new Date(b.metadata.created_at).getTime()
            : b.start_time
              ? new Date(b.start_time).getTime()
              : 0;
          break;
        case 'next_run_date':
          // Use next_run_date if present, otherwise fall back to end_time or 0.
          aValue = a.next_run_date ? new Date(a.next_run_date).getTime() : 0;
          bValue = b.next_run_date ? new Date(b.next_run_date).getTime() : 0;
          break;
        case 'success':
          aValue = Number(a.success_record_count || 0);
          bValue = Number(b.success_record_count || 0);
          break;
        case 'failure':
          aValue = Number(a.failure_record_count || 0);
          bValue = Number(b.failure_record_count || 0);
          break;
        default:
          return 0;
      }

      if (
        sortConfig.column === 'workflow_name' ||
        sortConfig.column === 'status'
      ) {
        if (sortConfig.direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else {
        // For dates and numbers
        return sortConfig.direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }
    });
  }, [filteredRows, sortConfig]);

  const handleViewLogs = (workflowRunId) => {
    setSelectedWorkflowRunId(workflowRunId);
    setLogsDialogOpen(true);
  };

  const handleCancelSchedule = async (workflowId) => {
    try {
      if (!workflowId) {
        setError('Missing workflow ID for cancellation');
        return;
      }
      setCanceling((prev) => ({ ...prev, [workflowId]: true }));
      await cancelWorkflowSchedule(workflowId);
      const wf = rows.find((r) => r.workflow_id === workflowId);
      const name = wf?.workflow_name || String(workflowId);
      toast.success(`Cancelled schedule for ${name}`, { autoClose: 3000 });
      // Refresh data so cancelled schedules drop from the list
      await loadData();
    } catch (e) {
      console.error('Cancel schedule error:', e);
      setError(e?.message || 'Failed to cancel schedule');
      toast.error(e?.message || 'Failed to cancel schedule', {
        autoClose: 4000,
      });
    } finally {
      setCanceling((prev) => ({ ...prev, [workflowId]: false }));
    }
  };

  const handleDownloadCSV = () => {
    const headers = [
      'Sr. No',
      'Workflow Name',
      'Status',
      'Created Date',
      'Next Run Date',
      'Source',
      'Destination',
      'Success Records',
      'Failure Records',
    ];

    // Prepare CSV rows
    const csvRows = sortedRows.map((row, idx) => {
      const createdDate = row.metadata?.created_at
        ? formatLocalDate(new Date(row.metadata.created_at))
        : row.start_time
          ? formatLocalDate(new Date(row.start_time))
          : '-';
      const nextRun = row.next_run_date
        ? formatLocalDate(new Date(row.next_run_date))
        : '-';
      const { source, destination } = getConnectionNames(row);

      return [
        idx + 1,
        `"${row.workflow_name || ''}"`,
        row.status || row.run_status || 'N/A',
        createdDate,
        nextRun,
        `"${source.join('; ')}"`,
        `"${destination.join('; ')}"`,
        row.success_record_count ?? 0,
        row.failure_record_count ?? 0,
      ].join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...csvRows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `scheduled_jobs_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Box sx={{ display: 'flex' }}>
        <Sidebar />
        <Box sx={{ flex: 1, p: 3, background: '#f8f9fa', minHeight: '100vh' }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#111827' }}
            >
              Schedule Job
            </Typography>
          </Box>

          {/* Filter container */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid #E5E7EB',
              borderRadius: 2,
              mb: 2,
              background: 'linear-gradient(to bottom, #ffffff, #F9FAFB)',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 600,
                color: '#374151',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 4,
                  height: 20,
                  backgroundColor: '#5965A6',
                  borderRadius: 1,
                  display: 'inline-block',
                }}
              />
              Filter Options
            </Typography>

            {/* Search and date filters */}
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
              }}
            >
              {/* From Date filter */}
              <TextField
                size="small"
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                sx={{
                  minWidth: 155,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
                InputLabelProps={{ shrink: true }}
              />

              {/* To Date filter */}
              <TextField
                size="small"
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                sx={{
                  minWidth: 155,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                size="small"
                label="Limit"
                type="number"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                inputProps={{ min: 1 }}
                sx={{
                  width: 90,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
              />
              {/* Search filter for workflow name */}
              <TextField
                size="small"
                label="Search"
                placeholder="Search workflow..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
              />

              {/* Limit filter */}
            </Box>
          </Paper>

          {/* Table */}
          <motion.div variants={itemVariants}>
            <Box
              component={motion.div}
              sx={{
                borderRadius: '12px',
                background: '#ffffff',
                border: '1px solid #E5E7EB',
                p: 0,
                mt: 1,
                mb: 2,
                position: 'relative',
                overflow: 'hidden',
                boxShadow:
                  '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              }}
            >
              {/* Table Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  borderBottom: '1px solid #E5E7EB',
                  backgroundColor: '#ffffff',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: '#111827',
                      mb: 0,
                    }}
                  >
                    Scheduled Workflow Runs
                  </Typography>
                </Box>
                <Tooltip title="Download as CSV" arrow placement="top">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadCSV}
                    disabled={sortedRows.length === 0}
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: '#5965A6',
                      borderColor: 'rgba(89, 101, 166, 0.3)',
                      textTransform: 'none',
                      px: 2,
                      py: 0.75,
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#5965A6',
                        backgroundColor: 'rgba(89, 101, 166, 0.08)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(89, 101, 166, 0.2)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      '&.Mui-disabled': {
                        color: '#9CA3AF',
                        borderColor: '#E5E7EB',
                      },
                    }}
                  >
                    Download
                  </Button>
                </Tooltip>
              </Box>

              <TableContainer
                sx={{ backgroundColor: '#ffffff', overflow: 'hidden' }}
              >
                <Table size="small">
                  <TableHead sx={{ backgroundColor: '#5965A6' }}>
                    <TableRow
                      sx={{
                        '&:hover': {
                          backgroundColor: '#4C5B9A',
                        },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          py: 1,
                          px: 2,
                          color: '#ffffff',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '1px solid #E5E7EB',
                          width: '60px',
                        }}
                      >
                        Sr. No
                      </TableCell>

                      {/* Sortable headers */}
                      {[
                        {
                          label: 'Workflow Name',
                          key: 'workflow_name',
                          sortable: true,
                          align: 'left',
                        },
                        {
                          label: 'Status',
                          key: 'status',
                          sortable: true,
                          align: 'left',
                        },
                        {
                          label: 'Created Date',
                          key: 'created_date',
                          sortable: true,
                          align: 'left',
                        },
                        {
                          label: 'Next Run Date',
                          key: 'next_run_date',
                          sortable: true,
                          align: 'left',
                        },
                        {
                          label: 'Source',
                          key: 'source',
                          sortable: false,
                          align: 'left',
                        },
                        {
                          label: 'Destination',
                          key: 'destination',
                          sortable: false,
                          align: 'left',
                        },
                        {
                          label: 'Success',
                          key: 'success',
                          sortable: true,
                          align: 'center',
                        },
                        {
                          label: 'Failure',
                          key: 'failure',
                          sortable: true,
                          align: 'center',
                        },
                      ].map((header) => (
                        <TableCell
                          key={header.key}
                          align={header.align}
                          onClick={
                            header.sortable
                              ? () => handleSort(header.key)
                              : undefined
                          }
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            py: 1,
                            px: 2,
                            color: '#ffffff',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #E5E7EB',
                            width: header.width || 'auto',
                            cursor: header.sortable ? 'pointer' : 'default',
                            userSelect: 'none',
                            position: 'relative',
                            '&:hover': header.sortable
                              ? {
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  transition: 'background-color 0.2s ease',
                                }
                              : {},
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent:
                                header.align === 'center'
                                  ? 'center'
                                  : 'space-between',
                            }}
                          >
                            {header.label}
                            {header.sortable &&
                              sortConfig.column === header.key &&
                              sortConfig.direction === 'asc' && (
                                <ArrowUpwardIcon
                                  sx={{ fontSize: '0.875rem', ml: 0.5 }}
                                />
                              )}
                            {header.sortable &&
                              sortConfig.column === header.key &&
                              sortConfig.direction === 'desc' && (
                                <ArrowDownwardIcon
                                  sx={{ fontSize: '0.875rem', ml: 0.5 }}
                                />
                              )}
                            {header.sortable &&
                              (!sortConfig.column ||
                                sortConfig.column !== header.key) && (
                                <UnfoldMoreIcon
                                  sx={{
                                    fontSize: '0.875rem',
                                    ml: 0.5,
                                    opacity: 0.5,
                                  }}
                                />
                              )}
                          </Box>
                        </TableCell>
                      ))}

                      {/* Logs header - not sortable */}
                      <TableCell
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          py: 1,
                          px: 2,
                          color: '#ffffff',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        Logs
                      </TableCell>
                      {/* Action header - not sortable */}
                      <TableCell
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          py: 1,
                          px: 2,
                          color: '#ffffff',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading &&
                      Array.from({ length: 6 }).map((_, i) => (
                        <TableRow
                          key={`sk-${i}`}
                          sx={{ borderBottom: '1px solid #F3F4F6' }}
                        >
                          {/* Render skeleton cells equal to the number of columns (11) */}
                          {Array.from({ length: 11 }).map((__, j) => (
                            <TableCell key={`skc-${i}-${j}`}>
                              <Skeleton variant="text" height={20} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}

                    {!loading && error && (
                      <TableRow>
                        <TableCell
                          colSpan={11}
                          align="center"
                          sx={{ py: 8, border: 'none' }}
                        >
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            <Typography
                              sx={{
                                color: '#DC2626',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                              }}
                            >
                              {error}
                            </Typography>
                          </motion.div>
                        </TableCell>
                      </TableRow>
                    )}

                    {!loading && !error && sortedRows.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={11}
                          align="center"
                          sx={{ py: 8, border: 'none' }}
                        >
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            <Typography
                              sx={{
                                color: '#9CA3AF',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                              }}
                            >
                              No scheduled runs found.
                            </Typography>
                          </motion.div>
                        </TableCell>
                      </TableRow>
                    )}

                    {!loading &&
                      !error &&
                      sortedRows.map((row, idx) => {
                        const status = row.status || row.run_status || 'N/A';
                        const createdDate = row.metadata?.created_at
                          ? formatLocalDate(new Date(row.metadata.created_at))
                          : row.start_time
                            ? formatLocalDate(new Date(row.start_time))
                            : '-';
                        const nextRunDate = row.next_run_date
                          ? formatLocalDate(new Date(row.next_run_date))
                          : '-';
                        const { source, destination } = getConnectionNames(row);

                        const colors = statusColorMap[status] || {
                          bg: '#6B7280',
                          bgLight: '#E5E7EB',
                          text: '#374151',
                          border: '#9CA3AF',
                        };

                        return (
                          <TableRow
                            // Ensure each row uses a truly unique key.  While
                            // workflow_run_id values should normally be
                            // unique, there have been instances where API
                            // responses contained duplicate IDs.  React
                            // warnings and duplicate rows can occur if keys
                            // collide.  Combine the workflow_run_id with
                            // the current index to guarantee uniqueness.
                            key={
                              row.workflow_run_id
                                ? `${row.workflow_run_id}-${idx}`
                                : `${idx}-${status}`
                            }
                            component={motion.tr}
                            custom={idx}
                            variants={rowVariants}
                            initial="hidden"
                            animate="visible"
                            whileHover="hover"
                            sx={{
                              borderBottom: '1px solid #F3F4F6',
                              backgroundColor:
                                idx % 2 === 1 ? '#F3F5F7' : '#ffffff',
                              '&:hover': {
                                backgroundColor:
                                  idx % 2 === 1 ? '#E3F2FD' : '#F9FAFB',
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                py: 0.25,
                                px: 2,
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: '#6B7280',
                              }}
                            >
                              {idx + 1}
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.25,
                                px: 2,
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: '#111827',
                              }}
                            >
                              {row.workflow_name || '-'}
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.25,
                                px: 2,
                              }}
                            >
                              <Chip
                                label={status === 'ACTIVE' ? 'RUNNING' : status}
                                size="small"
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  borderRadius: '16px',
                                  height: 28,
                                  minWidth: 80,
                                  backgroundColor: colors.bgLight,
                                  color: colors.text,
                                  border: `1px solid ${colors.border}`,
                                  textTransform: 'capitalize',
                                  '& .MuiChip-label': {
                                    px: 2.5,
                                    py: 0.25,
                                  },
                                }}
                              />
                            </TableCell>

                            <TableCell
                              sx={{
                                py: 0.25,
                                px: 2,
                                fontSize: '0.875rem',
                                color: '#6B7280',
                                fontWeight: 400,
                              }}
                            >
                              {createdDate}
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.25,
                                px: 2,
                                fontSize: '0.875rem',
                                color: '#6B7280',
                                fontWeight: 400,
                              }}
                            >
                              {nextRunDate}
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.25,
                                px: 2,
                                fontSize: '0.875rem',
                                color: '#6B7280',
                                fontWeight: 400,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5,
                                }}
                              >
                                {source.map((name, i) => (
                                  <Typography
                                    key={i}
                                    sx={{
                                      fontSize: '0.875rem',
                                      color: '#6B7280',
                                    }}
                                  >
                                    {name}
                                  </Typography>
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.25,
                                px: 2,
                                fontSize: '0.875rem',
                                color: '#6B7280',
                                fontWeight: 400,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5,
                                }}
                              >
                                {destination.map((name, i) => (
                                  <Typography
                                    key={i}
                                    sx={{
                                      fontSize: '0.875rem',
                                      color: '#6B7280',
                                    }}
                                  >
                                    {name}
                                  </Typography>
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                py: 0.25,
                                px: 2,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: statusColorMap.SUCCESS.text,
                              }}
                            >
                              {(row.success_record_count ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                py: 0.25,
                                px: 2,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: statusColorMap.FAILED.text,
                              }}
                            >
                              {(row.failure_record_count ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.25,
                                px: 2,
                              }}
                            >
                              <Tooltip title="View Logs" arrow placement="top">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleViewLogs(row.workflow_run_id)
                                  }
                                  sx={{
                                    color: '#5965A6',
                                    '&:hover': {
                                      backgroundColor:
                                        'rgba(89, 101, 166, 0.08)',
                                    },
                                  }}
                                >
                                  <Visibility sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            {/* Action cell: Cancel schedule */}
                            <TableCell
                              sx={{
                                py: 0.25,
                                px: 2,
                              }}
                            >
                              <Tooltip
                                title="Cancel Schedule"
                                arrow
                                placement="top"
                              >
                                <span>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    disabled={
                                      !row.workflow_id ||
                                      !!canceling[row.workflow_id]
                                    }
                                    onClick={() =>
                                      handleCancelSchedule(row.workflow_id)
                                    }
                                    sx={{
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      borderRadius: '8px',
                                      textTransform: 'none',
                                    }}
                                  >
                                    {canceling[row.workflow_id]
                                      ? 'Cancelling...'
                                      : 'Cancel'}
                                  </Button>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Logs Viewer Dialog */}
              <LogsViewer
                open={logsDialogOpen}
                onClose={() => setLogsDialogOpen(false)}
                workflowRunId={selectedWorkflowRunId}
              />
            </Box>
          </motion.div>
        </Box>
      </Box>
    </motion.div>
  );
};

export default ScheduleJob;
